import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SubscribeForm from '@/components/feature/SubscribeForm';
import FaqSection from '@/pages/home/components/FaqSection';
import { useSeo } from '@/hooks/useSeo';
import { editingStandards, faqs, principles, site, stats, testimonials } from '@/mocks/site';

const positioning = [
  {
    title: '我们做什么',
    icon: 'ri-focus-3-line',
    desc: '收集网络上的高热爆料，核对证据、追踪后续、给出判定与可信度评分。',
  },
  {
    title: '我们不做什么',
    icon: 'ri-forbid-2-line',
    desc: '不生产瓜、不抢先下结论、不为流量放大情绪。没有依据就写「仍在核实」。',
  },
  {
    title: '为谁而做',
    icon: 'ri-group-line',
    desc: '为看到热搜想确认真假的人、为不想被情绪裹挟转发的人、为需要引用参考的内容工作者。',
  },
];

export default function About() {
  const location = useLocation();

  useSeo({
    title: '关于我们 · 看料爆料的核实理念与联系投稿 | 看料爆料',
    description:
      '了解看料爆料的品牌定位、核实理念与内容原则。我们专注看料爆料真假核实、实锤追踪与辟谣，并提供投稿与纠错渠道，欢迎一起把料核准。',
    keywords: '关于看料爆料,看料爆料核实,看料爆料可信度,投稿爆料,纠错反馈',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于看料爆料',
        description: site.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系看料爆料',
        description: `投稿与纠错邮箱：${site.submitEmail}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  useEffect(() => {
    if (location.hash === '#submit') {
      const el = document.getElementById('submit');
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="container-page py-10 md:py-14">
            <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <p className="text-xs tracking-[0.3em] text-primary-600 font-medium">关于我们</p>
                <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[42px] md:leading-[1.2]">
                  不生产瓜，只把每一条料的真假核清楚
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600">{site.intro}</p>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-500">{site.slogan2}</p>
              </div>
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-3">
                  {stats.map((s) => (
                    <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-4">
                      <span className="flex w-9 h-9 items-center justify-center rounded-lg bg-primary-100 text-primary-600 text-lg">
                        <i className={s.icon}></i>
                      </span>
                      <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">{s.value}</p>
                      <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="positioning-title">
          <div className="container-page py-12 md:py-16">
            <h2 id="positioning-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              网站定位
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              看料爆料不是娱乐媒体，也不是情绪出口。我们更像一个把公开信息按核实流程过一遍的整理台，让每条料都有可查的依据。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {positioning.map((p) => (
                <div key={p.title} className="rounded-xl border border-background-200 bg-background-100 p-6">
                  <span className="flex w-11 h-11 items-center justify-center rounded-lg bg-primary-500 text-background-50 text-xl">
                    <i className={p.icon}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100" aria-labelledby="principles-title">
          <div className="container-page py-12 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-4">
                <p className="text-xs tracking-[0.3em] text-primary-600 font-medium">发展理念</p>
                <h2 id="principles-title" className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  内容原则与核实理念
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                  我们相信，核实本身就是一种内容能力。与其给出一个让人兴奋的结论，不如把判断的依据摊开，让读者自己看得见。
                </p>
                <div className="mt-6 rounded-xl border border-background-300 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">编辑标准</h3>
                  <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                    {editingStandards.map((s) => (
                      <li key={s} className="flex items-start gap-2">
                        <span className="mt-1 w-4 h-4 flex items-center justify-center text-primary-500 shrink-0">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="lg:col-span-8">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {principles.map((p) => (
                    <div key={p.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                      <span className="flex w-10 h-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 text-lg">
                        <i className={p.icon}></i>
                      </span>
                      <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{p.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50" aria-labelledby="voice-title">
          <div className="container-page py-12 md:py-16">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-xs tracking-[0.3em] text-primary-600 font-medium">用户口碑</p>
                <h2 id="voice-title" className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  读者是怎么用这个站的
                </h2>
              </div>
              <p className="max-w-md text-sm text-foreground-600">
                以下内容来自读者反馈，用于说明我们提供的参考方式，不代表任何平台立场。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="flex h-full flex-col rounded-xl border border-background-200 bg-background-100 p-6">
                  <span className="text-2xl text-accent-500">
                    <i className="ri-double-quotes-l"></i>
                  </span>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{t.quote}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="w-11 h-11 overflow-hidden rounded-full bg-background-200">
                      <img src={t.avatar} alt={`${t.name} 头像`} title={`${t.name} - 看料爆料读者`} className="w-full h-full object-cover object-top" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-foreground-950">{t.name}</span>
                      <span className="block text-xs text-foreground-500">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <FaqSection showMore={false} />

        <section id="submit" className="scroll-mt-28 border-y border-accent-200 bg-accent-50">
          <div className="container-page py-12 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-5">
                <p className="text-xs tracking-[0.3em] text-accent-700 font-medium">联系我们</p>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  投稿、纠错与订阅
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  无论你是想提供新线索、指出我们判定中的错误，还是只想订阅每周的核实周报，都可以用同一个表单联系我们。
                </p>

                <div className="mt-6 space-y-3">
                  <a
                    href={`mailto:${site.email}`}
                    className="flex items-center gap-3 rounded-xl border border-accent-200 bg-background-50 p-4 transition-colors hover:border-accent-400"
                  >
                    <span className="flex w-10 h-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700 text-lg">
                      <i className="ri-mail-line"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">合作与联系</span>
                      <span className="block text-sm font-medium text-foreground-900">{site.email}</span>
                    </span>
                  </a>
                  <a
                    href={`mailto:${site.submitEmail}`}
                    className="flex items-center gap-3 rounded-xl border border-accent-200 bg-background-50 p-4 transition-colors hover:border-accent-400"
                  >
                    <span className="flex w-10 h-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700 text-lg">
                      <i className="ri-send-plane-line"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">投稿爆料与纠错</span>
                      <span className="block text-sm font-medium text-foreground-900">{site.submitEmail}</span>
                    </span>
                  </a>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/ranking"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    浏览热料榜单
                  </Link>
                  <Link
                    to="/debunk"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 hover:border-secondary-500 hover:text-secondary-700 transition-colors whitespace-nowrap"
                  >
                    查看辟谣专题
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-7">
                <SubscribeForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}